import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { NumberInput } from './NumberInput';
import { Loader2 } from 'lucide-react';

/**
 * Generic Bulk Edit Dialog.
 *
 * Renders a list of field rows. Each row has its own enable-checkbox so the
 * user can pick exactly which fields to overwrite. Disabled fields are NOT
 * sent to the server (unchanged for selected records).
 *
 * Props:
 *   open                 — boolean
 *   onOpenChange(open)
 *   count                — number of selected items (shown in description)
 *   itemLabel            — "produk", "paket"
 *   fields               — array of field descriptors:
 *     {
 *       key:      "category",
 *       label:    "Kategori",
 *       type:     "text" | "number" | "boolean" | "select" | "textarea",
 *       options?: ["Sound","Lighting", ...]   // when type="select"
 *       placeholder?: string,
 *     }
 *   onSubmit(payload)    — called with object containing ONLY enabled fields,
 *                          numbers converted, booleans converted. Returns a
 *                          Promise; dialog stays open until resolved.
 */
export const BulkEditDialog = ({ open, onOpenChange, count, itemLabel = 'item', fields = [], onSubmit }) => {
  const [enabled, setEnabled] = useState({});       // key → boolean
  const [values, setValues] = useState({});         // key → raw string/value
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const e = {};
      const v = {};
      fields.forEach(f => {
        e[f.key] = false;
        v[f.key] = f.type === 'boolean' ? true : '';
      });
      setEnabled(e);
      setValues(v);
    }
  }, [open, fields]);

  const toggle = (k) => setEnabled(prev => ({ ...prev, [k]: !prev[k] }));
  const setVal = (k, v) => setValues(prev => ({ ...prev, [k]: v }));

  const anyEnabled = Object.values(enabled).some(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!anyEnabled) return;
    const payload = {};
    fields.forEach(f => {
      if (!enabled[f.key]) return;
      const raw = values[f.key];
      if (f.type === 'number') payload[f.key] = Number(raw) || 0;
      else if (f.type === 'boolean') payload[f.key] = Boolean(raw);
      else payload[f.key] = raw ?? '';
    });
    setSaving(true);
    try {
      await onSubmit(payload);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-lg max-h-[92vh] overflow-y-auto bg-card border-border" data-testid="bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Massal</DialogTitle>
          <DialogDescription>
            Aktifkan field yang ingin di-update untuk <span className="font-semibold text-foreground">{count} {itemLabel}</span> terpilih. Field yang tidak diaktifkan akan dibiarkan apa adanya.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 pt-1">
          {fields.map((f) => (
            <div
              key={f.key}
              className={`rounded-lg border p-3 transition-colors ${
                enabled[f.key] ? 'border-primary/40 bg-primary/5' : 'border-border bg-card/40'
              }`}
              data-testid={`bulk-edit-row-${f.key}`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  id={`bulk-${f.key}`}
                  checked={enabled[f.key] || false}
                  onCheckedChange={() => toggle(f.key)}
                  className="mt-1"
                  data-testid={`bulk-edit-toggle-${f.key}`}
                />
                <div className="flex-1 min-w-0 space-y-1.5">
                  <Label htmlFor={`bulk-${f.key}`} className="text-sm font-medium cursor-pointer">
                    {f.label}
                  </Label>
                  {enabled[f.key] && (
                    <div data-testid={`bulk-edit-input-${f.key}`}>
                      {f.type === 'number' && (
                        <NumberInput
                          value={values[f.key]}
                          onChange={(v) => setVal(f.key, v)}
                          className="h-11 md:h-9 text-base md:text-sm text-right font-mono-num"
                        />
                      )}
                      {f.type === 'text' && (
                        <Input
                          className="h-11 md:h-9 text-base md:text-sm"
                          placeholder={f.placeholder}
                          value={values[f.key] || ''}
                          onChange={(e) => setVal(f.key, e.target.value)}
                        />
                      )}
                      {f.type === 'textarea' && (
                        <Textarea
                          rows={3}
                          className="text-base md:text-sm"
                          placeholder={f.placeholder}
                          value={values[f.key] || ''}
                          onChange={(e) => setVal(f.key, e.target.value)}
                        />
                      )}
                      {f.type === 'boolean' && (
                        <div className="flex items-center gap-3 pt-0.5">
                          <Switch
                            checked={Boolean(values[f.key])}
                            onCheckedChange={(v) => setVal(f.key, v)}
                          />
                          <span className="text-sm text-muted-foreground">
                            {values[f.key] ? 'Aktif' : 'Nonaktif'}
                          </span>
                        </div>
                      )}
                      {f.type === 'select' && (
                        <Select
                          value={values[f.key] || ''}
                          onValueChange={(v) => setVal(f.key, v)}
                        >
                          <SelectTrigger className="h-11 md:h-9 text-base md:text-sm">
                            <SelectValue placeholder={f.placeholder || 'Pilih nilai'} />
                          </SelectTrigger>
                          <SelectContent>
                            {(f.options || []).map(opt => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" className="h-11 md:h-9 w-full sm:w-auto" onClick={() => onOpenChange(false)}>
              Batal
            </Button>
            <Button
              type="submit"
              disabled={!anyEnabled || saving}
              className="h-11 md:h-9 w-full sm:w-auto"
              data-testid="bulk-edit-submit"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Terapkan
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
